import { ICsvLeadRow } from '../../../services/apis/campaigns/useCampaignsRequest';

/**
 * Captures vanity slug from any /in/ profile URL (www, country subdomains, query, trailing slash).
 * The slug is NOT restricted to ASCII: real LinkedIn vanities contain diacritics, Cyrillic, CJK
 * and emoji (e.g. "marija-stanković-396251116", "дејан-шашковић-95bb8975").
 */
const LINKEDIN_IN_URL_RE = /(?:https?:\/\/)?(?:[\w-]+\.)?linkedin\.com\/in\/([^\s,;"'<>?#]+)/i;

/** Characters that can never appear inside a vanity slug — used to reject non-identifier cells. */
const NOT_IN_SLUG_RE = /[\s@/\\?#]/;
/** A slug must carry at least one letter or digit; "---" or "." alone is not an identifier. */
const HAS_ALNUM_RE = /[\p{L}\p{N}]/u;

/** Delimiters a pasted or exported list may use. */
const DELIMITERS = [',', ';', '\t'];

/**
 * Delimiter of the file: the candidate appearing most often in the first record, counting only
 * occurrences OUTSIDE quotes — a quoted "Sacramento, California" must not vote for the comma.
 */
function detectDelimiter(text: string): string {
  const counts: Record<string, number> = { ',': 0, ';': 0, '\t': 0 };
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') { i += 1; continue; }
      inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes) continue;
    if (ch === '\n') break;
    if (ch in counts) counts[ch] += 1;
  }
  return DELIMITERS.reduce((best, d) => (counts[d] > counts[best] ? d : best), ',');
}

/**
 * RFC 4180 tokenizer. Splitting on a bare /[,;\t]/ shifted every column right of a quoted cell
 * containing the delimiter ("Ship, ship | x-Google DeepMind"), which is how a Location cell ended
 * up being read as the LinkedIn identifier. A quote only opens a field at the start of that field,
 * so a stray quote inside free text (ex-"Big Co") stays literal; "" inside a quoted field is an
 * escaped quote; newlines inside a quoted field belong to the value.
 */
function parseRecords(text: string, delimiter: string): string[][] {
  const records: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];

    if (inQuotes) {
      if (ch !== '"') { field += ch; continue; }
      if (text[i + 1] === '"') { field += '"'; i += 1; continue; }
      inQuotes = false;
      continue;
    }

    if (ch === '"' && field.trim() === '') { field = ''; inQuotes = true; continue; }
    if (ch === delimiter) { row.push(field); field = ''; continue; }
    if (ch === '\r') continue;
    if (ch === '\n') { row.push(field); records.push(row); row = []; field = ''; continue; }
    field += ch;
  }

  row.push(field);
  records.push(row);

  return records
    .map(cells => cells.map(c => c.trim().replace(/^'(.*)'$/s, '$1').trim()))
    .filter(cells => cells.some(Boolean));
}

/** publicIdentifier from a LinkedIn profile URL, or '' when the cell is not one. */
function extractFromLinkedInUrl(cell: string): string {
  const m = cell.match(LINKEDIN_IN_URL_RE);
  if (!m) return '';
  // Drop trailing path noise (e.g. /in/slug/en) — vanity is the first segment.
  return decodeURIComponent(m[1].split('/')[0]).replace(/\/+$/, '');
}

/**
 * A cell that is usable as an identifier as-is. Applied to the column the header already
 * identified as the LinkedIn/publicIdentifier column, so it only has to reject obvious
 * non-identifiers ("N/A", emails, free text) — not to guess what a slug looks like.
 */
function isUsableIdentifier(cell: string): boolean {
  const value = cell.trim();
  return value.length >= 2 && !NOT_IN_SLUG_RE.test(value) && HAS_ALNUM_RE.test(value);
}

/**
 * A bare vanity slug (e.g. "hadji-younoussa-b50b80b2") — no URL around it, and no header telling
 * us which column holds it. Stricter than isUsableIdentifier because here we are guessing: it must
 * start with a letter or digit so a stray "Marketing" cell is less likely to be taken for a slug.
 */
function extractBareSlug(cell: string): string {
  return /^[\p{L}\p{N}][^\s@/\\?#]{2,}$/u.test(cell.trim()) ? cell.trim() : '';
}

// Normalized labels commonly seen in header rows. Compared against the WHOLE cell
// (not substrings), so a real slug like "hadji-first-b50b80b2" can never match.
const HEADER_LABELS = new Set([
  'pi', 'id', 'url', 'urls', 'link', 'links', 'slug', 'profile', 'profiles',
  'profileurl', 'profilelink', 'linkedin', 'linkedinurl', 'linkedinprofile',
  'linkedinprofileurl', 'personlinkedinurl', 'lnurl', 'liurl',
  'publicidentifier', 'vanity', 'vanityname', 'contact', 'connection', 'account',
  'first', 'firstname', 'last', 'lastname', 'name', 'fullname', 'email', 'emails',
  'company', 'title', 'headline', 'position', 'location', 'domain', 'website',
  'state', 'street', 'city', 'country', 'zip', 'phone', 'notes',
]);

/** Headers that identify the LinkedIn profile URL / vanity / publicIdentifier column. */
const LINKEDIN_COLUMN_LABELS = new Set([
  'linkedin', 'linkedinurl', 'linkedinprofile', 'linkedinprofileurl',
  'personlinkedinurl', 'profileurl', 'profilelink', 'lnurl', 'liurl',
  'publicidentifier', 'vanity', 'vanityname', 'pi', 'slug', 'url', 'urls',
  'link', 'links', 'profile', 'profiles',
]);

const FIELD_COLUMN_ALIASES: Record<keyof Pick<ICsvLeadRow, 'name' | 'firstName' | 'lastName' | 'title' | 'company' | 'location' | 'email'>, string[]> = {
  name:      ['name', 'fullname'],
  firstName: ['first', 'firstname'],
  lastName:  ['last', 'lastname'],
  // Aliases are listed most-wanted first (see fieldColumnIndexes): an export carrying both
  // "Job Title" and "Headline" should map title to the job title, not the headline blurb.
  title:     ['jobtitle', 'title', 'position', 'headline'],
  company:   ['company', 'organization', 'org'],
  location:  ['location', 'city', 'country'],
  // An address supplied here costs nothing to use: the lead is skipped by the
  // enrichment pre-flight entirely, so no credits are spent finding what you gave us.
  email:     ['email', 'emails', 'emailaddress', 'workemail', 'businessemail', 'personalemail', 'mail'],
};

/** Column labels that mean the address is a personal one rather than a work one. */
const PERSONAL_EMAIL_LABELS = new Set(['personalemail', 'privateemail', 'homeemail']);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const normalizeLabel = (cell: string): string => cell.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Header detection:
 *  - a row containing a real LinkedIn URL is always data, never a header;
 *  - otherwise it is a header when any cell equals a known column label
 *    (exact cell match after normalization — works for single-column headers
 *    like "LinkedIn" or "LinkedIn URL" as well as multi-column ones).
 */
function looksLikeHeaderRow(cells: string[]): boolean {
  const nonEmpty = cells.filter(Boolean);
  if (!nonEmpty.length) return false;
  if (nonEmpty.some(c => LINKEDIN_IN_URL_RE.test(c))) return false;
  return nonEmpty.some(c => HEADER_LABELS.has(normalizeLabel(c)));
}

/** Index of the LinkedIn URL/slug column from a header row, or -1. */
function linkedInColumnIndex(headerCells: string[]): number {
  return headerCells.findIndex(c => LINKEDIN_COLUMN_LABELS.has(normalizeLabel(c)));
}

/**
 * Map known profile field headers → column index. Alias order decides, not column order: the
 * first alias that matches any column wins, so "Job Title" is preferred over "Headline" no
 * matter which of the two the export happens to place first.
 */
function fieldColumnIndexes(headerCells: string[]): Partial<Record<keyof typeof FIELD_COLUMN_ALIASES, number>> {
  const labels = headerCells.map(normalizeLabel);
  const out: Partial<Record<keyof typeof FIELD_COLUMN_ALIASES, number>> = {};
  (Object.keys(FIELD_COLUMN_ALIASES) as (keyof typeof FIELD_COLUMN_ALIASES)[]).forEach(field => {
    for (const alias of FIELD_COLUMN_ALIASES[field]) {
      const idx = labels.indexOf(alias);
      if (idx >= 0) { out[field] = idx; break; }
    }
  });
  return out;
}

/**
 * Extract one lead identifier from a data row:
 *  1. URL from the LinkedIn / publicIdentifier header column when known;
 *  2. a LinkedIn profile URL in ANY column — a real URL beats a bare cell even when a header
 *     pointed elsewhere, so a row whose columns don't line up with the header (a ragged export)
 *     cannot turn a stray value like "California" into an identifier;
 *  3. the header column taken as-is (a file listing bare vanity slugs);
 *  4. otherwise a guessed bare slug from the first column.
 */
function rowIdentifier(cells: string[], linkedInIdx: number): string {
  if (linkedInIdx >= 0 && cells[linkedInIdx]) {
    const fromUrl = extractFromLinkedInUrl(cells[linkedInIdx]);
    if (fromUrl) return fromUrl;
  }
  for (const cell of cells) {
    const id = extractFromLinkedInUrl(cell);
    if (id) return id;
  }
  if (linkedInIdx >= 0 && cells[linkedInIdx] && isUsableIdentifier(cells[linkedInIdx])) {
    return cells[linkedInIdx].trim();
  }
  const slugIdx = linkedInIdx >= 0 ? linkedInIdx : 0;
  return cells[slugIdx] ? extractBareSlug(cells[slugIdx]) : '';
}

function cellAt(cells: string[], idx: number | undefined): string {
  if (idx === undefined || idx < 0) return '';
  return (cells[idx] || '').trim();
}

export interface IParsedCsv {
  rows: ICsvLeadRow[];
  /** Data rows no identifier could be read from \u2014 surfaced so they are not dropped silently. */
  skipped: number;
}

export function parseCsvText(raw: string): IParsedCsv {
  const text = String(raw ?? '').replace(/^\uFEFF/, '');
  if (!text.trim()) return { rows: [], skipped: 0 };
  const rows = parseRecords(text, detectDelimiter(text));
  if (!rows.length) return { rows: [], skipped: 0 };

  const hasHeader = looksLikeHeaderRow(rows[0]);
  const linkedInIdx = hasHeader ? linkedInColumnIndex(rows[0]) : -1;
  const fieldIdx = hasHeader ? fieldColumnIndexes(rows[0]) : {};
  const dataRows = hasHeader ? rows.slice(1) : rows;

  // "Personal email" is worth distinguishing: it changes what the campaign is allowed
  // to use, and personal addresses cost more to source than work ones.
  const emailIsPersonal = hasHeader
    && fieldIdx.email !== undefined
    && PERSONAL_EMAIL_LABELS.has(normalizeLabel(rows[0][fieldIdx.email] ?? ''));

  let skipped = 0;

  const parsed = dataRows
    .map(cells => {
      const id = rowIdentifier(cells, linkedInIdx);
      if (!id) { skipped += 1; return null; }

      const row: ICsvLeadRow = { publicIdentifier: id };

      const name = cellAt(cells, fieldIdx.name);
      const firstName = cellAt(cells, fieldIdx.firstName);
      const lastName = cellAt(cells, fieldIdx.lastName);
      const title = cellAt(cells, fieldIdx.title);
      const company = cellAt(cells, fieldIdx.company);
      const location = cellAt(cells, fieldIdx.location);

      if (name) row.name = name;
      if (firstName) row.firstName = firstName;
      if (lastName) row.lastName = lastName;
      if (!row.name && (firstName || lastName)) {
        row.name = `${firstName} ${lastName}`.trim();
      }
      if (title) row.title = title;
      if (company) row.company = company;
      if (location) row.location = location;

      /**
       * Prefer the column the header named; otherwise take the first cell that looks
       * like an address.
       *
       * The fallback matters because a pasted line usually has no header at all
       * ("some-identifier, someone@example.com"), and requiring one meant the address
       * was silently dropped. An email is safe to detect by shape — unlike a vanity
       * slug — and it can never be mistaken for the identifier, which rejects any
       * cell containing "@".
       */
      const email = (cellAt(cells, fieldIdx.email) || cells.find(c => EMAIL_RE.test(c.trim())) || '')
        .trim()
        .toLowerCase();

      if (email && EMAIL_RE.test(email)) {
        row.email = email;
        row.emailType = emailIsPersonal ? 'private' : 'business';
      }

      return row;
    })
    .filter((r): r is ICsvLeadRow => r !== null);

  return { rows: parsed, skipped };
}
